import { Alert, Button, Grid2, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import {
  $assessmentInfo,
  $questionIndex,
  $questions,
  nextPage,
  setCurrentQuestion,
} from 'store/assessment/model';
import ErrorIcon from 'assets/icons/ErrorIcon';
import { secondsToTimeStringFormat } from 'utils/secondsToMMSSFormat';
import { Trial_Question_Status, useStartQuestionMutation } from '@generated/graphql';
import { useToast } from '@myhrbox/myhrbox-frontend-core';
import Block from '../../components/Block';

interface SystemMessageProps {
  remainingTime: number | null;
}

const SystemMessage = ({ remainingTime }: SystemMessageProps) => {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const [questionIndex, assessmentInfo, questions] = useUnit([
    $questionIndex,
    $assessmentInfo,
    $questions,
  ]);

  const [startQuestion, { loading: startQuestionLoading }] = useStartQuestionMutation();

  const currentQuestion = questions[questionIndex];

  const questionOrder = currentQuestion.order + 1;
  const totalQuestionCount = questions.length;

  const timeForQuestion = currentQuestion.time - (currentQuestion.candidateTime || 0);

  const onGoToQuestion = () => {
    startQuestion({
      variables: {
        input: {
          questionId: currentQuestion.questionId,
          trialId: assessmentInfo!.id,
        },
      },
    })
      .then(({ data }) => {
        setCurrentQuestion(data?.startQuestion);
        nextPage();
      })
      .catch(() => {
        showErrorToast(t('errors.common'));
      });
  };

  return (
    <Stack spacing={3}>
      <Typography variant='h28'>{t('assessment.systemMessage.title')}</Typography>
      <Stack spacing={{ xs: 1.8, sm: 2.5 }}>
        <Block
          value={`${t('assessment.systemMessage.progress', {
            current: questionOrder,
            total: totalQuestionCount,
          })}${currentQuestion.status === Trial_Question_Status.Delayed ? ' (Отложенный)' : ''}`}
          label={t('assessment.systemMessage.question')}
        />
        <Block
          value={secondsToTimeStringFormat(timeForQuestion, t)}
          label={t('assessment.systemMessage.plannedTimeForQuestion')}
        />
        <Block
          value={
            remainingTime
              ? secondsToTimeStringFormat(remainingTime < 0 ? 0 : remainingTime, t)
              : '-'
          }
          label={t('assessment.systemMessage.remainingTimeForAssessment')}
        />
      </Stack>
      <Alert
        sx={{ backgroundColor: 'bgSwitch', p: 1 }}
        icon={<ErrorIcon sx={{ color: 'primary.main' }} />}>
        {t('assessment.systemMessage.info')}
      </Alert>
      <Grid2 container spacing={2} justifyContent='end'>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Button
            variant='contained'
            onClick={onGoToQuestion}
            size='large'
            fullWidth
            loading={startQuestionLoading}>
            {t('assessment.systemMessage.goToQuestion')}
          </Button>
        </Grid2>
      </Grid2>
    </Stack>
  );
};

export default SystemMessage;
